<template>
  <SearchModalTrigger ref="triggerEl" v-slot="{ openOverlay }" :is-mobile="isMobile">
    <form
      ref="formEl"
      class="nav-search-input grid-x"
      :class="{
        'search-modal-active': isSearchModalActive,
        'convo-commerce': isAiScriptEnabled,
        'mew-search-input-clean': isMobile && killswitches.masterMewWhitespaceRemovalEnabled,
        'search-bar-prominence': !isSearchModalActive && experiments.graySearchBarProminenceEnabled,
      }"
      :action="formAction"
      method="GET"
      @submit.prevent=""
      @keydown.esc="handleCloseModal"
      @keydown.arrow-down="handleArrowDown"
    >
      <Search
        v-model="searchValue"
        name="keyword"
        :placeholder="searchPlaceholderText"
        :pt="{ input: { autocomplete: 'off' } }"
        @on-input-click="openOverlay"
        @on-keypress="openOverlay"
        @on-enter="handleSubmit"
        @on-clear="handleOnClear"
        @on-cancel="handleOnClear"
      />
      <template v-if="isSearchModalActive">
        <button
          v-if="isMobile && killswitches.masterMewWhitespaceRemovalEnabled"
          type="button"
          class="nav-search-close"
          aria-label="Close search"
          @click="handleClose"
        >
          <i class="close-dark-medium"></i>
        </button>
        <button v-else type="button" class="close-normal close-large" aria-label="Close search" @click="handleClose"></button>
      </template>
    </form>
  </SearchModalTrigger>
</template>

<script setup>
/* v8 ignore file -- @preserve */
import { computed, watch, ref } from 'vue';
import { useDebounceFn, useEventBus } from '@vueuse/core';
import Search from '@nuxt-common/atomic-ui/app/components/search/Search';
import useSearchHistory from '~/composables/dumb/useSearchHistory';
import { FETCH_SEARCH_SUGGESTIONS, SET_SEARCH_MODAL_STATE, SET_SEARCH_VALUE } from '#shared/types/actions';
import { securityValidation } from '#shared/utils/StringUtil';
import getSearchPrefix from '~/utils/getSearchPrefix';
import { tagTypedSearch, tagFromHamburgerSearchBar } from '#shared/features/analytics/searchTags';
import { normalizeSearchString } from '~/utils/normalizeSearchString';
import { SEARCH_PAGE_PREFIXES } from '#shared/constants';
import addQueryParam from '~/utils/addQueryParam';
import { GET_IS_MOBILE_VIEW } from '#shared/types/getters';
import { useMobileNav } from '~/composables/useMobileNav';
import { events, SEARCH } from '#shared/constants/eventBus';
import useSearchPlaceholderText from '~/composables/dumb/useSearchPlaceholderText';
import useExperiments from '~/composables/useExperiments';
import useKillswitches from '~/composables/useKillswitches';
import useAskAiVisibility from '~/composables/useAskAiVisibility';
import SearchModalTrigger from '~/components/search/SearchModalTrigger.common.vue';

const formEl = ref(null);
const triggerEl = ref(null);
const experiments = useExperiments();
const killswitches = useKillswitches();
const { isAiScriptEnabled } = useAskAiVisibility();
const searchIsActivedWhileMobileMenuWasOpen = ref(false);

const { wasSideNavVisibleRecently } = useMobileNav();
const { isMcom, hostlink } = useEnvProps();
const { addToSearchHistory } = useSearchHistory();
const bus = useEventBus(SEARCH);
const navHeader = useNavHeader();
const navViewType = useNavViewType();
const navSearchSuggestions = useNavSearchSuggestions();

const formAction = `${hostlink}/shop/search`;
const isMobile = computed(() => navViewType[GET_IS_MOBILE_VIEW]);
const isSearchModalActive = computed(() => navHeader.isSearchModalActive);
const searchValue = computed({
  get() {
    return navHeader.searchValue;
  },
  set(value) {
    navHeader[SET_SEARCH_VALUE](value);
  },
});
const { newSearchPlaceholderText: searchPlaceholderText } = useSearchPlaceholderText(isMobile, isMcom);

// TODO (Refactor) move to SearchSuggestions component
const fetchSearchSuggestions = useDebounceFn((value) => {
  navSearchSuggestions[FETCH_SEARCH_SUGGESTIONS](value.trim());
}, 300);

function handleClose() {
  bus.emit(events[SEARCH].CLOSE);
}

function handleArrowDown() {
  bus.emit(events[SEARCH].KEYDOWN_ARROW_DOWN);
}

function handleSubmit(value) {
  if (securityValidation(value)) {
    const searchPrefix = getSearchPrefix();
    const normalizedSearchValue = normalizeSearchString(value, searchPrefix === SEARCH_PAGE_PREFIXES.featured);

    if (isMcom) tagTypedSearch(value);

    if (isMcom && isMobile.value && searchIsActivedWhileMobileMenuWasOpen.value) {
      tagFromHamburgerSearchBar();
    }

    addToSearchHistory(value);
    window.location.href = addQueryParam(`${hostlink}${searchPrefix}${normalizedSearchValue}`, { ss: true });
  }
}

function handleOnClear() {
  const inputElement = formEl.value.querySelector('.input-search');
  if (inputElement) {
    inputElement.focus();
  }
}

function handleCloseModal() {
  navHeader[SET_SEARCH_MODAL_STATE](false);
}

if (import.meta.client) {
  watch(searchValue, fetchSearchSuggestions, { immediate: true });
  watch(
    isSearchModalActive,
    (newValue) => {
      if (newValue) {
        // tracking if user open search while mobile menu was open
        searchIsActivedWhileMobileMenuWasOpen.value = wasSideNavVisibleRecently.value;
      }
    },
    { immediate: true },
  );
  bus.on((event) => {
    if (event === events[SEARCH].OPEN) {
      const inputElement = formEl.value?.querySelector('.input-search');

      if (inputElement) {
        triggerEl.value?.openOverlay();
        inputElement.focus();
      }
    }
  });
}
</script>

<style lang="scss">
.nav-search-input {
  width: 100%;

  &.search-modal-active {
    @include breakpoint(medium down) {
      & > div {
        margin-right: 40px;
      }
    }
  }

  & > div {
    width: 100%;

    .input-container {
      margin: 0;
    }
  }

  .close-normal {
    position: absolute;
    cursor: pointer;
    right: 0;
    top: 8px;

    @include breakpoint(large up) {
      display: none;
    }
  }
}

.nav-search-close {
  position: absolute;
  cursor: pointer;

  @include breakpoint(large up) {
    display: none;
  }
}
</style>

<style lang="scss" brand="mcom">
.nav-search-input {
  width: 100%;
  &.search-bar-prominence {
    .input-search {
      background: $background-color;
    }
  }
}

// right side gradient-fade for long placeholder text
.nav-search-input {
  &::after {
    content: '';
    position: absolute;
    top: 2px;
    right: 2px;
    bottom: 2px;
    width: 32px;
    pointer-events: none;
    border-radius: 100%;
    background: linear-gradient(to right, transparent, #fff 70%);
  }
  &.search-bar-prominence::after {
    background: linear-gradient(to right, transparent, $background-color 70%);
  }

  &.search-modal-active::after {
    display: none;
  }

  &:has(input:not(:placeholder-shown))::after {
    display: none;
  }
}

// mew condensed
.search-condensed {
  .input-search {
    padding: 8px 8px 8px 36px;
  }

  .padding-end-search-icon {
    padding-right: 30px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.nav-search-close {
  width: 32px;
  height: 32px;
  background-color: #fff;
  border-radius: 50%;
  top: 4px;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
}

.search-modal-active.mew-search-input-clean {
  & > div {
    animation: search-input-animation 400ms forwards;
  }

  .nav-search-close {
    animation: search-close-animation 600ms forwards;
  }
}

@keyframes search-input-animation {
  0% {
    opacity: 0;
    width: 70%;
  }

  10% {
    animation-timing-function: ease-out;
    opacity: 1;
    width: 70%;
  }

  100% {
    animation-timing-function: ease-out;
    width: 100%;
  }
}

@keyframes search-close-animation {
  0% {
    opacity: 0;
  }

  45% {
    opacity: 0;
  }

  85% {
    animation-timing-function: cubic-bezier(0.5, 0, 0.5, 1);
    opacity: 1;
  }

  100% {
    opacity: 1;
  }
}
</style>

<style lang="scss" brand="bcom">
.nav-search-input {
  padding: 0 8px;
  background-color: #fff;

  @include breakpoint(medium down) {
    padding: 0;
  }

  @include breakpoint(large only) {
    &.convo-commerce:not(.search-modal-active) {
      max-width: 290px;
    }
  }

  @supports (-webkit-touch-callout: none) {
    @media (pointer: coarse) {
      .input-search {
        font-size: 16px;
      }
    }
  }
}

.search-modal-active.mew-search-input-clean {
  padding: 7px 15px;
  border: 1px solid $gray-4-color;
  box-shadow: 1px 1px 2px 0 rgba(0, 0, 0, 0.1);
  animation: search-input-animation 280ms forwards;
}

@keyframes search-input-animation {
  0% {
    opacity: 0;
  }

  100% {
    animation-timing-function: cubic-bezier(0.5, 0, 0.5, 1);
    opacity: 1;
  }
}

.nav-search-close {
  top: 16px;
  right: 16px;
}
</style>
